import { useCallback, useMemo } from 'react';
import { useUserData } from '../components/UserDataContext';
import { compileMutedWordsRegex, itemIsMuted, groupIsMuted } from '../utils/mutedWords';

/**
 * Single source of truth for "should this content be hidden from this viewer?".
 *
 * Blocking used to be enforced in only three places (Discover, opening a DM, and
 * sending a DM), so a blocked user's yaps, replies, shouts, group posts, chats
 * and notifications all stayed visible. Every surface that renders
 * user-generated content should filter through this hook so the rule is applied
 * identically everywhere.
 *
 * Content carries the author id under several different keys depending on where
 * it came from (`userId` on yaps/comments, `authorId` on anonymous yaps,
 * `shoutedBy` on shouts, `senderId` on notifications/messages), so
 * `isBlockedContent` checks all of them — a blocked author must not slip through
 * just because a particular collection names the field differently.
 *
 * `isHiddenContent` is the predicate call sites should prefer. Today it is
 * exactly `isBlockedContent`; it exists as a seam so that additional
 * viewer-scoped hiding rules (muted words) can be added in one place rather than
 * being sprinkled across surfaces again — which is what caused the original
 * blocking gaps.
 */
export function useContentFilter() {
  const { blockedUsers, blockedBy, mutedWords, userProfile } = useUserData();

  // Both directions: people I blocked (blockedUsers) and people who blocked me
  // (blockedBy, mirrored onto my user doc by the onBlockedUsersChanged trigger).
  // Hiding only the first half meant a blocked user could still read and reply to
  // everything the person who blocked them posted.
  const blockedSet = useMemo(
    () => new Set([...(blockedUsers || []), ...(blockedBy || [])].map((id) => String(id))),
    [blockedUsers, blockedBy],
  );

  /** True when this user id is blocked in either direction. */
  const isBlockedId = useCallback(
    (id?: string | number | null) => !!id && blockedSet.has(String(id)),
    [blockedSet],
  );

  /**
   * True when a yap / comment / shout / notification / conversation was authored
   * by (or re-shared by, or sent by) someone the current user has blocked.
   */
  const isBlockedContent = useCallback(
    (item: any): boolean => {
      if (!item || blockedSet.size === 0) return false;
      // Deliberately does NOT look at item.id: on a yap/comment that's the
      // content id, not a user id. For plain user objects call isBlockedId(user.id).
      const candidates = [
        item.userId,
        item.authorId,
        item.shoutedBy,
        item.senderId,
        item.fromUserId,
        item.ownerId,
        item.friendId,     // chat list entries key the other participant this way
        item.otherUserId,
        // A shout embeds the original yap; its author must be checked too, or a
        // blocked author reaches the feed via anyone else's shout of them.
        item.originalYap?.userId,
        item.originalYap?.authorId,
      ];
      return candidates.some((c) => c != null && blockedSet.has(String(c)));
    },
    [blockedSet],
  );

  // ── Muted words ────────────────────────────────────────────────────────────
  // One alternation regex for the whole list, rebuilt only when the list
  // changes: a 100-word list then costs a single test per item instead of 100.
  const mutedRegex = useMemo(() => compileMutedWordsRegex(mutedWords), [mutedWords]);

  /** True when this item's text (or a shout's caption / quoted yap) is muted. */
  const isMutedContent = useCallback(
    (item: any): boolean => {
      if (!mutedRegex || !item) return false;
      // Never hide the viewer's own posts. Someone who mutes a word they
      // themselves use should not watch their own yaps disappear.
      const ownId = userProfile?.id != null ? String(userProfile.id) : null;
      if (ownId && (String(item.userId) === ownId || String(item.authorId) === ownId)) return false;
      return itemIsMuted(item, mutedRegex);
    },
    [mutedRegex, userProfile?.id],
  );

  /**
   * True when a forum/tribe should be hidden from DISCOVERY surfaces because its
   * name or description is muted. Groups the viewer already belongs to are never
   * hidden — muting shapes what gets recommended, not what you are a member of.
   */
  const isMutedGroup = useCallback(
    (group: any): boolean => {
      if (!mutedRegex || !group) return false;
      const joinedIds = new Set([
        ...((userProfile?.joinedForumIds as string[]) || []),
        ...((userProfile?.joinedTribeIds as string[]) || []),
      ].map(String));
      return groupIsMuted(group, mutedRegex, (id) => joinedIds.has(String(id)));
    },
    [mutedRegex, userProfile?.joinedForumIds, userProfile?.joinedTribeIds],
  );

  /**
   * The predicate every content surface should use: hidden because the author is
   * blocked, OR because the text carries a muted word. Kept as one function so
   * surfaces never have to remember to apply two separate filters — the mistake
   * that left blocking inconsistent in the first place.
   */
  const isHiddenContent = useCallback(
    (item: any): boolean => isBlockedContent(item) || isMutedContent(item),
    [isBlockedContent, isMutedContent],
  );

  /**
   * Convenience: drop every hidden item from a list.
   *
   * Returns the original array unchanged when there is nothing to filter, rather
   * than always allocating. Several callers compute this during render, so
   * handing back a fresh array every time would churn referential identity for
   * no reason.
   */
  // Nothing to hide at all — must account for BOTH rules, or turning on mute
  // would be silently skipped for anyone with an empty block list.
  const filtersNothing = blockedSet.size === 0 && mutedRegex === null;

  const filterVisible = useCallback(
    <T,>(items: T[] | null | undefined): T[] =>
      !items || filtersNothing ? (items || []) : items.filter((i) => !isHiddenContent(i)),
    [filtersNothing, isHiddenContent],
  );

  /** Drop muted groups from a discovery list (search, trending, recommendations). */
  const filterVisibleGroups = useCallback(
    <T,>(groups: T[] | null | undefined): T[] =>
      !groups || !mutedRegex ? (groups || []) : groups.filter((g) => !isMutedGroup(g)),
    [mutedRegex, isMutedGroup],
  );

  return {
    blockedSet,
    isBlockedId,
    isBlockedContent,
    isMutedContent,
    isMutedGroup,
    isHiddenContent,
    filterVisible,
    filterVisibleGroups,
    /** @deprecated Use filterVisible. Retained so existing call sites keep working. */
    filterBlocked: filterVisible,
    hasBlocks: blockedSet.size > 0,
    hasMutedWords: mutedRegex !== null,
    /** True when neither rule can hide anything — lets callers skip work entirely. */
    filtersNothing,
  };
}
